import toast, { type ToastOptions } from "react-hot-toast"
import type { CSSProperties } from "react"

const DEFAULT_DURATION_MS = 2000

export const TOAST_BASE_STYLE: CSSProperties = {
  borderRadius: "30px",
  padding: "14px 22px",
  fontSize: "16px",
  fontWeight: 700,
  maxWidth: "min(90vw, 360px)",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
  textAlign: "center",
}

const TOAST_VARIANTS = {
  default: {
    background: "rgba(0, 0, 0, 0.75)",
    color: "#fff",
  },
  success: {
    background: "rgba(22, 163, 74, 0.92)",
    color: "#fff",
  },
  error: {
    background: "rgba(220, 38, 38, 0.92)",
    color: "#fff",
  },
  warning: {
    background: "rgba(217, 119, 6, 0.92)",
    color: "#fff",
  },
} as const

type ToastVariant = keyof typeof TOAST_VARIANTS

function showVariantToast(
  message: string,
  variant: ToastVariant,
  duration = DEFAULT_DURATION_MS,
  onClose?: () => void,
) {
  const text = message?.trim()
  if (!text) return

  const options: ToastOptions = {
    duration,
    style: {
      ...TOAST_BASE_STYLE,
      ...TOAST_VARIANTS[variant],
    },
  }

  if (variant === "success") toast.success(text, options)
  else if (variant === "error") toast.error(text, options)
  else toast(text, options)

  if (typeof onClose === "function") {
    setTimeout(onClose, duration)
  }
}

export function showDefaultToast(message: string, duration?: number, onClose?: () => void) {
  showVariantToast(message, "default", duration && duration > 0 ? duration : DEFAULT_DURATION_MS, onClose)
}

export function showSuccessToast(message: string, onClose?: () => void) {
  showVariantToast(message, "success", DEFAULT_DURATION_MS, onClose)
}

export function showErrorToast(message: string, onClose?: () => void) {
  showVariantToast(message, "error", DEFAULT_DURATION_MS, onClose)
}

export function showWarningToast(message: string, onClose?: () => void) {
  showVariantToast(message, "warning", DEFAULT_DURATION_MS, onClose)
}

export type AlertLikeInput =
  | string
  | Error
  | { title?: string; message?: string; onClose?: () => void }

export function resolveAlertMessage(input: AlertLikeInput | unknown, fallback: string): string {
  if (input == null) return fallback
  if (typeof input === "string") return input.trim() || fallback
  if (input instanceof Error) return input.message?.trim() || fallback
  if (typeof input === "object" && "message" in input) {
    const message = (input as { message?: unknown }).message
    if (typeof message === "string" && message.trim()) return message.trim()
  }
  return fallback
}

export function resolveAlertOnClose(input: AlertLikeInput | unknown): (() => void) | undefined {
  if (input && typeof input === "object" && !(input instanceof Error) && "onClose" in input) {
    const fn = (input as { onClose?: () => void }).onClose
    return typeof fn === "function" ? fn : undefined
  }
  return undefined
}
